/**
 * TypedSignature Component
 *
 * Lets the user type their name and choose a cursive font.
 * Renders the text on a hidden canvas and returns a base64 PNG
 * (same format as drawn signatures).
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';

const FONTS = [
  { name: 'Dancing Script', label: 'Dancing Script' },
  { name: 'Great Vibes', label: 'Great Vibes' },
  { name: 'Pacifico', label: 'Pacifico' },
  { name: 'Sacramento', label: 'Sacramento' },
  { name: 'Satisfy', label: 'Satisfy' },
  { name: 'Allura', label: 'Allura' },
  { name: 'Yellowtail', label: 'Yellowtail' },
  { name: 'Alex Brush', label: 'Alex Brush' },
];

export default function TypedSignature({ onCapture, width = 400, height = 150 }) {
  const [name, setName] = useState('');
  const [selectedFont, setSelectedFont] = useState(FONTS[0].name);
  const canvasRef = useRef(null);

  // Render text onto the hidden canvas whenever name or font changes
  const renderSignature = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    if (!name.trim()) return;

    // Scale font size to fit the canvas width
    let fontSize = 48;
    ctx.font = `${fontSize}px '${selectedFont}', cursive`;
    let measured = ctx.measureText(name);
    while (measured.width > canvas.width - 20 && fontSize > 16) {
      fontSize -= 2;
      ctx.font = `${fontSize}px '${selectedFont}', cursive`;
      measured = ctx.measureText(name);
    }

    ctx.fillStyle = '#000000';
    ctx.textBaseline = 'middle';
    ctx.fillText(name, 10, canvas.height / 2);
  }, [name, selectedFont]);

  useEffect(() => {
    renderSignature();
  }, [renderSignature]);

  const handleCapture = () => {
    if (!name.trim()) return;
    renderSignature();
    const canvas = canvasRef.current;
    if (!canvas) return;

    const result = {
      imageBase64: canvas.toDataURL('image/png'),
      timestamp: new Date().toISOString(),
    };
    onCapture?.(result);
  };

  return (
    <div>
      {/* Name input */}
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Type your full name"
        className="w-full border rounded px-3 py-2 mb-3 text-sm"
      />

      {/* Font picker */}
      <div className="flex flex-wrap gap-2 mb-3">
        {FONTS.map((f) => (
          <button
            key={f.name}
            onClick={() => setSelectedFont(f.name)}
            className={`px-3 py-1 rounded border text-sm ${
              selectedFont === f.name
                ? 'border-blue-600 bg-blue-50 text-blue-700'
                : 'border-gray-300 hover:border-gray-400'
            }`}
            style={{ fontFamily: `'${f.name}', cursive`, fontSize: '16px' }}
          >
            {name || 'Preview'}
          </button>
        ))}
      </div>

      {/* Preview canvas */}
      <div className="border rounded bg-white mb-3" style={{ width: Math.min(width, 400) }}>
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          style={{ width: '100%', height: 'auto' }}
        />
      </div>

      {/* Capture button */}
      <button
        onClick={handleCapture}
        disabled={!name.trim()}
        className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
      >
        Use This Signature
      </button>
    </div>
  );
}
